import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Observable, Subject, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { RouteConstants } from '../../../../core/constants/routes';
import { FocusHelper } from '../../../../core/helpers/focus/focus-helper';
import { ScrollHelper } from '../../../../core/helpers/scroll/scroll-helper';
import { ApplicationState } from '../../../../core/models/application-state/application-state';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../core/models/form-group-type';
import { DirectEntryStepPaths } from '../../../../modules/application/direct-entry/shared/constants/direct-entry-steps';
import { ApplicationPageFacadeService } from '../../../../modules/application/direct-entry/shared/services/application-page-facade/application-page-facade.service';
import { ApplicationValidationHelper } from '../../../../modules/application/shared/helpers/application-validator/application-validator.helper';
import { StepLink } from '../../../../modules/application/shared/models/step-link';
import { StepValidationResult } from '../../../../modules/application/shared/models/step-validation-result';

/**
 * A base class for all existing (Wizard, Direct Entry, and Template) pages. This class handles all of the
 * repeated logic and flows that are common across all pages.
 */
@Component({
    selector: 'app-application-page-base',
    changeDetection: ChangeDetectionStrategy.Eager,
    template:
        '<!-- This component serves as a base and does not render any content -->',
})
export abstract class ApplicationPageBaseComponent<
        TRequest,
        TResponse,
        TFormModel,
    >
    implements OnInit, OnDestroy
{
    protected destroy$ = new Subject<void>();
    private readonly hostElementRef = inject(ElementRef<HTMLElement>);

    abstract form:
        | FormGroup<FormGroupType<TFormModel>>
        | FormGroup<NestedFormGroupType<TFormModel>>;

    applicationState: ApplicationState | null =
        this.applicationPageFacadeService.applicationStateService.getCurrentApplicationState();
    hasChanges: boolean = false;
    applicationId: string | undefined;

    constructor(
        protected readonly route: ActivatedRoute,
        protected readonly router: Router,
        protected readonly applicationPageFacadeService: ApplicationPageFacadeService,
    ) {}

    ngOnInit(): void {
        ScrollHelper.scrollToTopOfPage();

        this.loadForm()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.setupReadOnly();

                this.applicationPageFacadeService.formEntryService.handleFormDirtying<TFormModel>(
                    this.form,
                    this.destroy$,
                    (newValue: boolean) => {
                        // Don't track changes if the form is read-only
                        if (!this.isReadOnly()) {
                            this.hasChanges = newValue;
                        }
                    },
                );

                this.handleNextClick();
                this.handleSaveAndExitClick();
                this.handlePreviousClick();
                this.handleProceedToNotesClick();
                this.handleProceedToHandoverToEwaClick();
                this.handleProceedToStep();
                this.handleSubmitToFccClick();
                this.handleSubmitToPscaClick();
                this.handleSubmitToFiveDayHoldClick();
                this.handleCreateTemplateClick();
                this.handleDeleteClick();
                this.postFormBindingActions();
                this.focusFirstField();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected loadForm(): Observable<void> {
        return this.route.parent!.paramMap.pipe(
            takeUntil(this.destroy$),
            switchMap((params: ParamMap) => {
                this.applicationId = params.get('id')!;
                return this.getFormDataFromApi(this.applicationId);
            }),
            tap((response: TResponse) => {
                this.onLoad(response);
            }),
            map(() => void 0),
        );
    }

    private handleProceedToNotesClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.applicationActionService
                .proceedToNotes$,
            () => {
                return this.router.navigate([
                    RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                        DirectEntryStepPaths.attachmentsAndWaivers,
                        this.applicationId!,
                    ),
                ]);
            },
        );
    }

    private handleProceedToHandoverToEwaClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.stepNavigationService
                .handoverToEwa$,
            () => {
                return this.router.navigate([
                    RouteConstants.Application.getDirectEntryFullRouteWithApplicationId(
                        DirectEntryStepPaths.submitToEwa,
                        this.applicationId!,
                    ),
                ]);
            },
        );
    }

    private handleSubmitToFccClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.stepNavigationService
                .submitToFccClick$,
            () => {
                const userId =
                    this.applicationState?.applicationUserInformation?.userId;

                this.applicationPageFacadeService.applicationSubmitToFccService
                    .submitToFcc(this.applicationId!, userId ?? null)
                    .subscribe();
            },
        );
    }

    private handleSubmitToPscaClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.stepNavigationService
                .submitToPscaClick$,
            () => {
                const userId =
                    this.applicationState?.applicationUserInformation?.userId;

                this.applicationPageFacadeService.applicationSubmitToPscaService
                    .submitToPsca(this.applicationId!, userId ?? null)
                    .subscribe();
            },
        );
    }

    private handleSubmitToFiveDayHoldClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.stepNavigationService
                .submitToFiveDayHoldClick$,
            () => {
                this.applicationPageFacadeService.applicationSubmitToFccService.openFiveDayHoldDialog(
                    this.applicationId!,
                );
            },
        );
    }

    /**
     * Handles the click event for the "Create Template" button when the application is not a template.
     */
    private handleCreateTemplateClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.applicationActionService
                .createTemplate$,
            () => {
                return this.applicationPageFacadeService.applicationTemplateFormDialogService.displayCreationFromApplicationFormDialog();
            },
        );
    }

    private handleDeleteClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.applicationActionService
                .deleteClick$,
            () => {
                if (
                    this.applicationPageFacadeService.applicationStateService.IsApplicationTemplate()
                ) {
                    this.applicationPageFacadeService.applicationDeletionService.deleteApplicationTemplate(
                        this.applicationState!.applicationTemplateInformation!
                            .applicationTemplateId,
                    );
                } else {
                    this.applicationPageFacadeService.applicationDeletionService.deleteApplication(
                        this.applicationId!,
                    );
                }
            },
        );
    }

    private handleProceedToStep(): void {
        this.handleAction(
            this.applicationPageFacadeService.stepNavigationService
                .proceedingToStep$,
            (stepLink: StepLink) => {
                this.router.navigate([stepLink.url]);
            },
        );
    }

    protected handleSaveAndExitClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.stepNavigationService
                .saveAndClose$,
            () => {
                const returnRoute =
                    this.applicationPageFacadeService.returnRouteService.consume();
                if (returnRoute) {
                    this.router.navigate([returnRoute]);
                } else {
                    this.router.navigate([
                        RouteConstants.Dashboard.getFullRoute('INDEX'),
                    ]);
                }
            },
        );
    }

    protected handleNextClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.stepNavigationService.nextClick$,
            () => {
                this.applicationPageFacadeService.stepNavigationService.goToNextStep();
            },
        );
    }

    protected handlePreviousClick(): void {
        this.handleAction(
            this.applicationPageFacadeService.stepNavigationService
                .previousClick$,
            () => {
                this.applicationPageFacadeService.stepNavigationService.goToPreviousStep();
            },
        );
    }

    /**
     * (OPTIONAL) This method can be overridden in the child class to handle any extra actions that are specific to the page that
     * need to occur AFTER the form is initially loaded.
     */
    protected postFormBindingActions(): void {}

    /**
     * Moves keyboard focus to the first field on the page after it loads. This ensures that
     * regardless of how the user navigated to the page (Next, Previous, or the Step Menu), their
     * focus starts on the first field.
     */
    protected focusFirstField(): void {
        if (!this.shouldAutoFocusFirstField()) {
            return;
        }

        FocusHelper.deferFocusFirstField(this.hostElementRef.nativeElement);
    }

    /**
     * (OPTIONAL) Override in a child class to opt out of automatically focusing the first field on load.
     * This is useful for pages that manage their own focus (e.g. multi-step sub-wizards).
     */
    protected shouldAutoFocusFirstField(): boolean {
        return true;
    }

    /**
     * (OPTIONAL) This method can be overridden in the child class to handle any extra actions that are specific to the page that
     * need to occur AFTER the form is successfully saved. By default, it returns an Observable that completes immediately.
     *
     * @return An Observable that completes when post-save actions are done.
     */
    protected postSaveActions(): Observable<void> {
        return of(void 0);
    }

    /**
     * Initializes and enforces read-only behavior for the current application context.
     *
     * When the current application is marked as read-only:
     * - Disables the root FormGroup (cascading to all child controls) without emitting value change events.
     * - Resets hasChanges to prevent save operations, navigation guards, or step transitions from triggering.
     *
     * This method should be invoked once during initialization since the read-only state
     * does not change during the lifetime of the page.
     */
    private setupReadOnly(): void {
        if (this.isReadOnly()) {
            this.applicationPageFacadeService.applicationFormControlManager.disable(
                this.form,
                { emitEvent: false },
            );
            this.hasChanges = false;
        }
    }

    /**
     * Determines if the application is in read-only mode via the ApplicationFormControlManagerService.
     *
     * @return true if the application is read-only, false otherwise.
     */
    protected isReadOnly(): boolean {
        return this.applicationPageFacadeService.applicationFormControlManager.isReadOnly();
    }

    private handleAction<T extends StepLink | void>(
        action$: Observable<T>,
        onSuccess: (value: T) => void,
    ): void {
        action$
            .pipe(
                takeUntil(this.destroy$),
                switchMap((value: T) => {
                    //  If no changes OR read-only (form disabled), don't save
                    if (!this.hasChanges || this.isReadOnly()) {
                        // If there are no changes, we don't need to save the data
                        // but we still want to proceed with the `onSuccess` action.
                        return of(value);
                    }

                    const request = this.generateRequestObject();
                    return this.saveFormDataToApi(request).pipe(
                        tap((stepValResult: StepValidationResult) => {
                            this.applicationPageFacadeService.stepNavigationService.setCurrentStepValidation(
                                stepValResult.passedFinalValidation,
                            );
                        }),
                        switchMap(() => {
                            /**
                             * Post-save actions specific to the page can be handled here. The pipeline will wait for these actions to complete
                             * before proceeding to the next steps in the case that any post-save actions are asynchronous.
                             */
                            return this.postSaveActions();
                        }),
                        tap(() => {
                            // Setting the flag to "false" allows the page navigation to proceed and not be blocked by the unsaved changes.
                            // See: BlockNavigationIfChangeGuard logic applied on all application pages.
                            this.hasChanges = false;
                        }),
                        map(() => value), // Pass through the original value
                    );
                }),
            )
            .subscribe({
                next: (value) => {
                    onSuccess(value);
                },
            });
    }

    private onLoad(response: TResponse): void {
        this.bindForm(response);

        const currentStep: StepLink | undefined =
            this.applicationPageFacadeService.stepNavigationService.getCurrentStep();

        if (currentStep?.passedValidation === undefined) {
            return;
        }

        ApplicationValidationHelper.markAllRequiredAsTouched(this.form);
    }

    /**
     * Logic to bind the response data to the page's form.
     * @param response
     */
    protected abstract bindForm(response: TResponse): void;

    /**
     * API call to get the form data for the page.
     * @param applicationId The application's Id.
     */
    protected abstract getFormDataFromApi(
        applicationId: string,
    ): Observable<TResponse>;

    /**
     * API call to save the form data to the server.
     * @param request
     */
    protected abstract saveFormDataToApi(
        request: TRequest,
    ): Observable<StepValidationResult>;

    /**
     * Generates the request object (from the form) in preparate to send the data to the server during saving actions.
     */
    protected abstract generateRequestObject(): TRequest;
}
